<script setup lang="ts">
import axios from 'axios'
import { notifyError } from '@/utils/helpers'
import type { ECOption } from '@/plugins/echarts'



const TB_BASE_URL = import.meta.env.VITE_TB_URL
const theme = useTheme()
const isLoading = ref(false)
const isLoadingChart = ref(false)

// ── ThingsBoard auth ──────────────────────────────────────────────────────────

const tbToken = ref<string | null>(null)

async function getTbToken(): Promise<string | null> {
    if (tbToken.value) return tbToken.value
    try {
        const resp = await apiOmni.post('api/tb/login')
        tbToken.value = resp.data.token
        return tbToken.value
    } catch (e: unknown) {
        notifyError(e)
        return null
    }
}

function tbHeaders(token: string): Record<string, string> {
    return {
        'X-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
    }
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DEVICE_TYPE = 'SW3L'
const CUMUL_KEY = 'data_Total_pulse'
const DAY_MS = 86_400_000
const HOUR_MS = 3_600_000

const LUX_DEVICE_ID = 'a68c7460-26ee-11f0-841a-eb7529e3a150'
const LUX_KEY = 'data_Light_lux'

const TANK_DEVICE_ID = '9c791fb0-1a0f-11ef-a1ac-0544cc92861d'
const TANK_KEY = 'data_gh_current_irrig_tank'

function toGallons(rawDelta: number): number {
    return (rawDelta / 12) * 0.264172
}

// ── Types ─────────────────────────────────────────────────────────────────────

declare interface TbDevice {
    id: { id: string; entityType: string }
    name: string
    type: string
    label: string
}

declare interface TbPageData<T> {
    data: T[]
    totalPages: number
    totalElements: number
    hasNext: boolean
}

declare interface DeviceSummary {
    device: TbDevice
    totalGallons: number
    lastTs: number | null
}

declare interface TankInterval {
    startTs: number
    endTs: number
    tank: 'eau' | 'engrais'
}

declare interface TbRelation {
    from: { entityType: string; id: string }
    to: { entityType: string; id: string }
    type: string
}

// ── State ─────────────────────────────────────────────────────────────────────

const today = new Date()
const sevenDaysAgo = new Date(today)
sevenDaysAgo.setDate(today.getDate() - 6)
const dateFrom = ref(sevenDaysAgo.toISOString().slice(0, 10))
const dateTo = ref(today.toISOString().slice(0, 10))

const allDevices = ref<TbDevice[]>([])
const deviceSummaries = ref<DeviceSummary[]>([])
const selectedDevice = ref<TbDevice | null>(null)
const activeId = ref<string | null>(null)
const deviceSearch = ref('')
const chartOption = ref<ECOption | null>(null)

// Lux line smoothing: 0 = no smoothing, 10 = very smooth
// Stored as a percent (0–1) passed to ECharts smooth
const luxSmoothing = ref(3)
const tempSmoothing = ref(3)

// Cache last loaded chart data so we can rebuild without a network call
const _cachedLabel = ref<string>('')
const _cachedDays = ref<{ label: string; startTs: number; data: [number, number][] }[]>([])
const _cachedLux = ref<{ ts: number; value: number }[]>([])
const _cachedTank = ref<TankInterval[]>([])
const _cachedTemp = ref<{ ts: number; value: number }[]>([])
const _cachedGallonsPerRun = ref<number | null>(null)

// Inline edit state for gallons_per_run
const gallonsPerRunEdit = ref<string>('')
const isSavingGpr = ref(false)

async function applyGallonsPerRun() {
    const v = parseFloat(gallonsPerRunEdit.value)
    if (isNaN(v) || !selectedDevice.value) return
    isSavingGpr.value = true
    try {
        const tok = await getTbToken()
        if (tok) {
            await saveGallonsPerRun(tok, selectedDevice.value.id.id, v)
            _cachedGallonsPerRun.value = v
            chartOption.value = buildChartOption(
                _cachedLabel.value,
                _cachedDays.value,
                _cachedLux.value,
                _cachedTank.value,
                luxSmoothing.value,
                _cachedTemp.value,
                tempSmoothing.value,
                v,
            )
        }
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingGpr.value = false
    }
}

const globalTotalGallons = computed(() =>
    deviceSummaries.value.reduce((s, d) => s + d.totalGallons, 0),
)

const filteredSummaries = computed<DeviceSummary[]>(() => {
    const q = deviceSearch.value.trim().toLowerCase()
    if (!q) return deviceSummaries.value
    return deviceSummaries.value.filter(s =>
        (s.device.label || s.device.name).toLowerCase().includes(q),
    )
})

// ── Data fetching ─────────────────────────────────────────────────────────────

async function fetchSW3LDevices(token: string): Promise<TbDevice[]> {
    const PAGE_SIZE = 100
    let page = 0
    const result: TbDevice[] = []
    while (true) {
        const url = `${TB_BASE_URL}/api/tenant/devices?pageSize=${PAGE_SIZE}&page=${page}&type=${DEVICE_TYPE}`
        const resp = await axios.get<TbPageData<TbDevice>>(url, { headers: tbHeaders(token) })
        result.push(...resp.data.data)
        if (!resp.data.hasNext) break
        page++
    }
    return result.sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name))
}

async function fetchPulse(
    token: string,
    deviceId: string,
    startTs: number,
    endTs: number,
): Promise<{ ts: number; value: number }[]> {
    const url =
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
        `?keys=${CUMUL_KEY}&startTs=${startTs}&endTs=${endTs}&limit=10000&orderBy=ASC`
    const resp = await axios.get(url, { headers: tbHeaders(token) })
    const raw: { ts: number; value: string }[] = resp.data?.[CUMUL_KEY] ?? []
    return raw.map(r => ({ ts: r.ts, value: parseFloat(r.value) }))
}

async function fetchSeedPulse(
    token: string,
    deviceId: string,
    beforeTs: number,
): Promise<number | null> {
    const url =
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
        `?keys=${CUMUL_KEY}&endTs=${beforeTs}&limit=1`
    const resp = await axios.get(url, { headers: tbHeaders(token) })
    const raw = resp.data?.[CUMUL_KEY]?.[0]?.value
    return raw !== undefined ? parseFloat(raw) : null
}

async function fetchLuxData(
    token: string,
    startTs: number,
    endTs: number,
): Promise<{ ts: number; value: number }[]> {
    const url =
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${LUX_DEVICE_ID}/values/timeseries` +
        `?keys=${LUX_KEY}&startTs=${startTs}&endTs=${endTs}&limit=10000&orderBy=ASC`
    const resp = await axios.get(url, { headers: tbHeaders(token) })
    const raw: { ts: number; value: string }[] = resp.data?.[LUX_KEY] ?? []
    return raw.map(r => ({ ts: r.ts, value: parseFloat(r.value) }))
}

async function fetchTankHistory(
    token: string,
    startTs: number,
    endTs: number,
): Promise<{ ts: number; value: string }[]> {
    const url =
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${TANK_DEVICE_ID}/values/timeseries` +
        `?keys=${TANK_KEY}&startTs=${startTs}&endTs=${endTs}&limit=10000&orderBy=ASC`
    const resp = await axios.get(url, { headers: tbHeaders(token) })
    return resp.data?.[TANK_KEY] ?? []
}

/**
 * Walk the relations of a SW3L device to find its parent greenhouse (a DEVICE entity).
 */
async function fetchParentGreenhouseDevice(token: string, deviceId: string): Promise<string | null> {
    try {
        const resp = await axios.get<TbRelation[]>(
            `${TB_BASE_URL}/api/relations?toId=${deviceId}&toType=DEVICE`,
            { headers: tbHeaders(token) },
        )
        const rel = resp.data.find(r => r.from.entityType === 'DEVICE')
        return rel ? rel.from.id : null
    } catch {
        return null
    }
}

async function fetchTemperatureData(
    token: string,
    deviceId: string,
    startTs: number,
    endTs: number,
): Promise<{ ts: number; value: number }[]> {
    const url =
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
        `?keys=temperature&startTs=${startTs}&endTs=${endTs}&limit=10000&orderBy=ASC`
    const resp = await axios.get(url, { headers: tbHeaders(token) })
    const raw: { ts: number; value: string }[] = resp.data?.['temperature'] ?? []
    return raw.map(r => ({ ts: r.ts, value: parseFloat(r.value) }))
}

async function saveGallonsPerRun(token: string, deviceId: string, value: number): Promise<void> {
    await axios.post(
        `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/SERVER_SCOPE`,
        { gallons_per_run: value },
        { headers: tbHeaders(token) },
    )
}

async function fetchGallonsPerRun(token: string, deviceId: string): Promise<number | null> {
    try {
        const resp = await axios.get(
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/attributes`,
            { headers: tbHeaders(token) },
        )
        const raw = resp.data as Array<{ key: string; value: unknown }>
        const val = raw.find(r => r.key === 'gallons_per_run')?.value
        return val !== undefined && val !== null ? Number(val) : null
    } catch {
        return null
    }
}

// ── Range helpers ─────────────────────────────────────────────────────────────

function getRange(): { startTs: number; endTs: number } {
    const startTs = new Date(dateFrom.value).getTime()
    const endTs = new Date(dateTo.value + 'T23:59:59').getTime()
    return { startTs, endTs }
}

/** Midnight (local) for a given timestamp */
function dayMidnight(ts: number): number {
    const d = new Date(ts)
    d.setHours(0, 0, 0, 0)
    return d.getTime()
}

// ── Summary computation ───────────────────────────────────────────────────────

/**
 * Compute total gallons consumed over the window for a device.
 * Handles counter resets (delta < 0) by ignoring them.
 */
function computeTotal(
    readings: { ts: number; value: number }[],
    seed: number | null,
): number {
    let total = 0
    let prev = seed
    for (const r of readings) {
        if (prev !== null) {
            const delta = r.value - prev
            if (delta > 0) total += toGallons(delta)
        }
        prev = r.value
    }
    return total
}

// ── Chart computation ─────────────────────────────────────────────────────────

/**
 * Build hourly time-series where the Y axis resets to 0 at each midnight.
 * Returns one series per day, each series containing hourly cumulative values
 * that start at 0 at midnight and grow until EOD.
 */
function buildDailyResetSeries(
    readings: { ts: number; value: number }[],
    seed: number | null,
    startTs: number,
    endTs: number,
): { days: { label: string; startTs: number; data: [number, number][] }[] } {
    // Group hours into day buckets
    const firstMidnight = dayMidnight(startTs)
    const days: { label: string; startTs: number; hourly: Map<number, number> }[] = []
    for (let m = firstMidnight; m <= endTs; m += DAY_MS) {
        const d = new Date(m)
        const label = d.toLocaleDateString('fr-CA', { weekday: 'short', month: 'short', day: 'numeric' })
        days.push({ label, startTs: m, hourly: new Map() })
    }

    // Accumulate gallons per day, per hour
    let prev = seed
    for (const r of readings) {
        if (prev !== null) {
            const delta = r.value - prev
            if (delta > 0) {
                const gallons = toGallons(delta)
                const hourTs = Math.floor(r.ts / HOUR_MS) * HOUR_MS
                const midnight = dayMidnight(r.ts)
                const day = days.find(d => d.startTs === midnight)
                if (day) {
                    day.hourly.set(hourTs, (day.hourly.get(hourTs) ?? 0) + gallons)
                }
            }
        }
        prev = r.value
    }

    // Convert hourly deltas to hourly cumulative within each day
    const result = days.map(day => {
        const data: [number, number][] = []
        let cum = 0
        // Fill every hour from midnight to now (or end of day)
        const dayEnd = Math.min(day.startTs + DAY_MS, endTs)
        for (let h = day.startTs; h <= dayEnd; h += HOUR_MS) {
            const delta = day.hourly.get(h) ?? 0
            cum += delta
            data.push([h, parseFloat(cum.toFixed(3))])
        }
        return { label: day.label, startTs: day.startTs, data }
    })

    return { days: result }
}

/**
 * Convert sorted tank telemetry into colored time intervals.
 * Consecutive same-tank readings are merged into one band.
 * A 'false' reading closes the current band.
 */
function buildTankIntervals(
    raw: { ts: number; value: string }[],
    endTs: number,
): TankInterval[] {
    const intervals: TankInterval[] = []
    let cur: TankInterval | null = null

    for (let i = 0; i < raw.length; i++) {
        const v = raw[i].value.toLowerCase().trim()
        const tank = (v === 'eau' || v === 'engrais') ? v as 'eau' | 'engrais' : null
        const nextTs = i < raw.length - 1 ? raw[i + 1].ts : endTs

        if (!tank) {
            // pump off — close the current band at this timestamp
            if (cur) {
                cur.endTs = raw[i].ts
                intervals.push(cur)
                cur = null
            }
        } else if (cur && cur.tank === tank) {
            // same tank active — extend band to the next reading
            cur.endTs = nextTs
        } else {
            // tank changed — push previous band, start a new one
            if (cur) intervals.push(cur)
            cur = { startTs: raw[i].ts, endTs: nextTs, tank }
        }
    }
    if (cur) intervals.push(cur)
    return intervals
}

/**
 * Resample lux readings by averaging over a time window.
 * smoothing 0 = raw, 1–10 maps to 5–180 min windows.
 */
function resampleLux(
    data: { ts: number; value: number }[],
    smoothing: number,
): { ts: number; value: number }[] {
    if (smoothing === 0 || data.length === 0) return data
    const windowMinutes = [0, 5, 10, 15, 20, 30, 45, 60, 90, 120, 180][smoothing]
    const windowMs = windowMinutes * 60_000
    const result: { ts: number; value: number }[] = []
    let bucketStart = Math.floor(data[0].ts / windowMs) * windowMs
    let sum = 0
    let count = 0
    for (const pt of data) {
        if (pt.ts >= bucketStart + windowMs) {
            if (count > 0) result.push({ ts: bucketStart + windowMs / 2, value: sum / count })
            const skipped = Math.floor((pt.ts - bucketStart) / windowMs)
            bucketStart += skipped * windowMs
            sum = 0
            count = 0
        }
        sum += pt.value
        count++
    }
    if (count > 0) result.push({ ts: bucketStart + windowMs / 2, value: sum / count })
    return result
}

function buildChartOption(
    deviceLabel: string,
    days: { label: string; startTs: number; data: [number, number][] }[],
    luxData: { ts: number; value: number }[],
    tankIntervals: TankInterval[],
    smoothing: number,
    tempData: { ts: number; value: number }[],
    tempSmoothing: number,
    gallonsPerRun: number | null,
): ECOption {
    const sampledLux = resampleLux(luxData, smoothing)
    const sampledTemp = resampleLux(tempData, tempSmoothing)
    const isDark = theme.global.name.value === 'dark'
    const labelColor = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)'
    const splitLineColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

    const palette = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336']

    const waterSeries = days.map((day, i) => ({
        name: day.label,
        type: 'line' as const,
        smooth: false,
        symbol: 'none',
        color: palette[i % palette.length],
        yAxisIndex: 0,
        data: day.data,
        emphasis: { focus: 'series' as const },
        ...(i === 0 && gallonsPerRun ? {
            markLine: {
                silent: true,
                symbol: 'none',
                lineStyle: { color: 'rgba(255,255,255,0.5)', type: 'dashed' as const, width: 1 },
                label: { formatter: `1 run (${gallonsPerRun} gal)`, position: 'insideEndTop' as const, fontSize: 10, color: 'rgba(255,255,255,0.7)' },
                data: [{ yAxis: gallonsPerRun }],
            },
        } : {}),
    }))

    const luxSeries = {
        name: 'Luminosité',
        type: 'line' as const,
        smooth: false,
        symbol: 'none',
        color: '#FFC107',
        yAxisIndex: 1,
        lineStyle: { width: 1.5, opacity: 0.7 },
        areaStyle: { color: 'rgba(255, 193, 7, 0.06)' },
        data: sampledLux.map(r => [r.ts, r.value] as [number, number]),
        emphasis: { focus: 'series' as const },
        markArea: {
            silent: true,
            data: tankIntervals.map(iv => [
                {
                    xAxis: iv.startTs,
                    itemStyle: {
                        color: iv.tank === 'eau'
                            ? 'rgba(33, 150, 243, 0.10)'
                            : 'rgba(76, 175, 80, 0.10)',
                    },
                },
                { xAxis: iv.endTs },
            ] as [{ xAxis: number; itemStyle: { color: string } }, { xAxis: number }]),
        },
    }

    const tempSeries = {
        name: 'Température',
        type: 'line' as const,
        smooth: false,
        symbol: 'none',
        color: '#F44336',
        yAxisIndex: 2,
        lineStyle: { width: 1.5, opacity: 0.85 },
        data: sampledTemp.map(r => [r.ts, r.value] as [number, number]),
        emphasis: { focus: 'series' as const },
    }

    const series = [...waterSeries, luxSeries, ...(tempData.length ? [tempSeries] : [])]

    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' as const },
            formatter(params: unknown) {
                const items = params as Array<{ seriesName: string; value: [number, number]; marker: string; yAxisIndex?: number }>
                if (!items.length) return ''
                const ts = items[0].value[0]
                const d = new Date(ts)
                const dateStr = d.toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })
                const timeStr = `${String(d.getHours()).padStart(2, '0')}h${String(d.getMinutes()).padStart(2, '0')}`
                const lines = items.map(it => {
                    if (it.seriesName === 'Luminosité') {
                        return `${it.marker} ${it.seriesName}: <b>${it.value[1].toFixed(0)} lux</b>`
                    }                    if (it.seriesName === 'Temp\u00e9rature') {
                        return `${it.marker} ${it.seriesName}: <b>${it.value[1].toFixed(1)} \u00b0C</b>`
                    }                    return `${it.marker} ${it.seriesName}: <b>${it.value[1].toFixed(2)} gal</b>`
                })
                return `<b>${dateStr} ${timeStr}</b><br/>${lines.join('<br/>')}`
            },
        },
        legend: {
            top: 4,
            textStyle: { color: labelColor },
            data: ['Luminosité', 'Température'],
        },
        grid: { top: 48, left: '2%', right: '2%', bottom: 48, containLabel: true },
        dataZoom: [
            { type: 'slider', bottom: 4, height: 20, borderColor: 'transparent', xAxisIndex: 0 },
            { type: 'inside', xAxisIndex: 0, zoomOnMouseWheel: true, moveOnMouseMove: false, moveOnMouseWheel: false },
        ],
        xAxis: {
            type: 'time',
            axisLabel: {
                color: labelColor,
                fontSize: 10,
                hideOverlap: true,
                formatter: (val: number) => {
                    const d = new Date(val)
                    const h = d.getHours()
                    const hStr = `${String(h).padStart(2, '0')}h`
                    if (h === 0) {
                        // midnight: show weekday + day/month on two lines
                        const dateStr = d.toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })
                        return `{bold|${dateStr}}\n${hStr}`
                    }
                    return hStr
                },
                rich: {
                    bold: { fontWeight: 'bold' as const, fontSize: 10 },
                },
            },
            splitLine: {
                show: true,
                interval: (index: number, value: string) => new Date(Number(value)).getHours() === 0,
                lineStyle: { color: splitLineColor, type: 'dashed' as const },
            },
        },
        yAxis: [
            {
                type: 'value',
                name: 'gal',
                nameTextStyle: { color: labelColor, fontSize: 10 },
                axisLabel: { color: labelColor, fontSize: 10, formatter: (v: number) => v.toFixed(1) },
                splitLine: { lineStyle: { color: splitLineColor } },
                min: 0,
                max: 600,
            },
            {
                type: 'value',
                name: 'lux',
                nameTextStyle: { color: '#FFC107', fontSize: 10 },
                axisLabel: { color: '#FFC107', fontSize: 10, formatter: (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v)) },
                splitLine: { show: false },
                min: 0,
            },
            {
                type: 'value',
                name: '\u00b0C',
                nameTextStyle: { color: '#F44336', fontSize: 10 },
                axisLabel: { color: '#F44336', fontSize: 10, formatter: (v: number) => `${v.toFixed(1)}` },
                splitLine: { show: false },
                position: 'right',
                offset: 55,
                min: 0,
                max: 50,
            },
        ],
        series,
    }
}

// ── Load all summaries on mount ───────────────────────────────────────────────

async function loadSummaries(token: string) {
    const { startTs, endTs } = getRange()
    deviceSummaries.value = []
    chartOption.value = null
    selectedDevice.value = null
    activeId.value = null

    const devices = await fetchSW3LDevices(token)
    allDevices.value = devices

    const results = await Promise.all(
        devices.map(async dev => {
            try {
                const [readings, seed] = await Promise.all([
                    fetchPulse(token, dev.id.id, startTs, endTs),
                    fetchSeedPulse(token, dev.id.id, startTs),
                ])
                const totalGallons = computeTotal(readings, seed)
                const lastTs = readings.length ? readings[readings.length - 1].ts : null
                return { device: dev, totalGallons, lastTs } as DeviceSummary
            } catch {
                return { device: dev, totalGallons: 0, lastTs: null } as DeviceSummary
            }
        }),
    )
    deviceSummaries.value = results
}

// ── Load chart for selected device ───────────────────────────────────────────

async function loadChart(device: TbDevice) {
    const token = await getTbToken()
    if (!token) return
    isLoadingChart.value = true
    chartOption.value = null
    try {
        const { startTs, endTs } = getRange()
        const [readings, seed, luxData, tankRaw, gallonsPerRun] = await Promise.all([
            fetchPulse(token, device.id.id, startTs, endTs),
            fetchSeedPulse(token, device.id.id, startTs),
            fetchLuxData(token, startTs, endTs),
            fetchTankHistory(token, startTs, endTs),
            fetchGallonsPerRun(token, device.id.id),
        ])
        // Fetch greenhouse parent device + its temperature in sequence (parent needed first)
        const parentId = await fetchParentGreenhouseDevice(token, device.id.id)
        const tempData = parentId ? await fetchTemperatureData(token, parentId, startTs, endTs) : []
        const { days } = buildDailyResetSeries(readings, seed, startTs, endTs)
        const tankIntervals = buildTankIntervals(tankRaw, endTs)
        // Cache data for slider-driven rebuilds
        _cachedLabel.value = device.label || device.name
        _cachedDays.value = days
        _cachedLux.value = luxData
        _cachedTank.value = tankIntervals
        _cachedTemp.value = tempData
        _cachedGallonsPerRun.value = gallonsPerRun
        gallonsPerRunEdit.value = gallonsPerRun !== null ? String(gallonsPerRun) : ''
        chartOption.value = buildChartOption(device.label || device.name, days, luxData, tankIntervals, luxSmoothing.value, tempData, tempSmoothing.value, gallonsPerRun)
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingChart.value = false
    }
}

function selectDevice(summary: DeviceSummary) {
    selectedDevice.value = summary.device
    activeId.value = summary.device.id.id
    loadChart(summary.device)
}

// ── Duration helper ───────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
    const minutes = Math.floor(ms / 60000)
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} h ${minutes % 60} min`
    const days = Math.floor(hours / 24)
    return `${days} j ${hours % 24} h`
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

async function reloadAll() {
    if (isLoading.value) return
    isLoading.value = true
    try {
        const token = await getTbToken()
        if (token) await loadSummaries(token)
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

// Rebuild chart when smoothing changes (no network call needed)
watch([luxSmoothing, tempSmoothing], () => {
    if (_cachedDays.value.length > 0) {
        chartOption.value = buildChartOption(
            _cachedLabel.value,
            _cachedDays.value,
            _cachedLux.value,
            _cachedTank.value,
            luxSmoothing.value,
            _cachedTemp.value,
            tempSmoothing.value,
            _cachedGallonsPerRun.value,
        )
    }
})

onMounted(reloadAll)
</script>

<template>
    <v-card
        class="mx-4 my-4 page-card"
        rounded="xl"
        :loading="isLoading"
        style="height: 85vh; display: flex; flex-direction: column;">

        <!-- Page header -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
                <div class="d-flex align-center" style="gap: 12px;">
                    <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="M19,14.5C19,14.5 21,16.67 21,18A2,2 0 0,1 19,20A2,2 0 0,1 17,18C17,16.67 19,14.5 19,14.5M5,18V9A2,2 0 0,1 3,7A2,2 0 0,1 5,5V4A2,2 0 0,1 7,2H9A2,2 0 0,1 11,4V5H19A2,2 0 0,1 21,7V9L21,11A1,1 0 0,1 22,12A1,1 0 0,1 21,13H17A1,1 0 0,1 16,12A1,1 0 0,1 17,11V9H11V18H12A2,2 0 0,1 14,20V22H2V20A2,2 0 0,1 4,18H5Z" size="26" />
                    </v-avatar>
                    <div>
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Consommation d'eau</div>
                        <div class="text-caption text-medium-emphasis">Irrigation par serre — consommation cumulée journalière</div>
                    </div>
                </div>

                <!-- Date range + reload -->
                <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                    <v-text-field
                        v-model="dateFrom"
                        label="Du"
                        type="date"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="width: 150px;" />
                    <v-text-field
                        v-model="dateTo"
                        label="Au"
                        type="date"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="width: 150px;" />
                    <v-btn
                        color="primary"
                        variant="tonal"
                        :loading="isLoading"
                        prepend-icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
                        @click="reloadAll">
                        Charger
                    </v-btn>
                    <v-chip
                        v-if="!isLoading && deviceSummaries.length > 0"
                        color="primary"
                        variant="tonal"
                        size="large"
                        prepend-icon="M12,20A6,6 0 0,1 6,14C6,10 12,3.25 12,3.25C12,3.25 18,10 18,14A6,6 0 0,1 12,20Z"
                        style="font-weight: 600;">
                        Total : {{ globalTotalGallons.toFixed(1) }} gal
                    </v-chip>
                </div>
            </div>
        </div>

        <v-divider />

        <!-- Two-pane layout -->
        <div class="d-flex flex-grow-1" style="overflow: hidden; min-height: 0;">

            <!-- Left: device list -->
            <div class="device-panel d-flex flex-column" style="width: 300px; min-width: 260px; border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));">
                <div class="pa-3">
                    <v-text-field
                        v-model="deviceSearch"
                        label="Filtrer les serres"
                        density="compact"
                        variant="outlined"
                        rounded="lg"
                        prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
                        hide-details
                        clearable />
                </div>

                <v-divider />

                <div class="flex-grow-1 overflow-y-auto">
                    <!-- Loading skeletons -->
                    <template v-if="isLoading">
                        <v-list-item v-for="i in 8" :key="i">
                            <v-skeleton-loader type="list-item-two-line" />
                        </v-list-item>
                    </template>

                    <!-- Device list -->
                    <v-list v-else density="compact" nav>
                        <v-list-item
                            v-for="summary in filteredSummaries"
                            :key="summary.device.id.id"
                            :active="activeId === summary.device.id.id"
                            active-color="primary"
                            rounded="lg"
                            class="mb-1"
                            @click="selectDevice(summary)">
                            <template #prepend>
                                <v-avatar size="32" rounded="md" :color="activeId === summary.device.id.id ? 'primary' : 'surface-variant'" variant="tonal">
                                    <v-icon icon="M12 3L4 9V21H20V9L12 3M10 10H14V19H10V10M16 10H18V13H16V10M15.33 8H8.67L12 5.5L15.33 8M8 10V13H6V10H8M6 15H8V19H6V15M16 19V15H18V19H16Z" size="16" />
                                </v-avatar>
                            </template>

                            <v-list-item-title class="text-body-2 font-weight-medium">
                                {{ summary.device.label || summary.device.name }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="d-flex align-center" style="gap: 6px;">
                                <v-icon icon="M12,3.77L11.25,4.61C11.25,4.61 9.97,6.06 8.68,7.94C7.39,9.82 6,12.07 6,14.23A6,6 0 0,0 12,20.23A6,6 0 0,0 18,14.23C18,12.07 16.61,9.82 15.32,7.94C14.03,6.06 12.75,4.61 12.75,4.61L12,3.77M12,6.9C12.44,7.42 12.84,7.85 13.68,9.07C14.89,10.83 16,13.07 16,14.23C16,16.45 14.22,18.23 12,18.23C9.78,18.23 8,16.45 8,14.23C8,13.07 9.11,10.83 10.32,9.07C11.16,7.85 11.56,7.42 12,6.9Z" size="12" color="primary" />
                                <span>{{ summary.totalGallons.toFixed(1) }} gal</span>
                                <span
                                    v-if="summary.lastTs"
                                    class="text-medium-emphasis">
                                    · {{ formatDuration(Date.now() - summary.lastTs) }}
                                </span>
                            </v-list-item-subtitle>

                            <template #append>
                                <v-icon icon="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" size="16" color="medium-emphasis" />
                            </template>
                        </v-list-item>

                        <v-list-item v-if="filteredSummaries.length === 0 && !isLoading">
                            <v-list-item-title class="text-caption text-medium-emphasis">
                                Aucune serre trouvée
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </div>
            </div>

            <!-- Right: chart panel -->
            <div class="flex-grow-1 d-flex flex-column pa-4" style="overflow-y: auto; min-width: 0;">

                <!-- No selection -->
                <template v-if="!selectedDevice && !isLoadingChart">
                    <div class="d-flex flex-column align-center justify-center" style="height: 100%; gap: 16px;">
                        <v-icon icon="M12 3L4 9V21H20V9L12 3M10 10H14V19H10V10M16 10H18V13H16V10M15.33 8H8.67L12 5.5L15.33 8M8 10V13H6V10H8M6 15H8V19H6V15M16 19V15H18V19H16Z" size="72" color="medium-emphasis" />
                        <div class="text-h6 text-medium-emphasis">Sélectionnez une serre</div>
                        <div class="text-body-2 text-medium-emphasis text-center" style="max-width: 320px;">
                            Choisissez une serre dans la liste pour afficher sa consommation d'eau sur les 5 derniers jours.
                        </div>
                    </div>
                </template>

                <!-- Loading chart -->
                <template v-else-if="isLoadingChart">
                    <div class="d-flex flex-column" style="gap: 16px;">
                        <v-skeleton-loader type="heading" />
                        <v-skeleton-loader type="image" height="360" />
                    </div>
                </template>

                <!-- Chart -->
                <template v-else-if="selectedDevice && chartOption">
                    <!-- Device header card -->
                    <v-card variant="outlined" rounded="lg" class="mb-4">
                        <v-card-title class="d-flex align-center" style="gap: 10px;">
                            <v-avatar color="primary" variant="tonal" size="36" rounded="lg">
                                <v-icon icon="M12 3L4 9V21H20V9L12 3M10 10H14V19H10V10M16 10H18V13H16V10M15.33 8H8.67L12 5.5L15.33 8M8 10V13H6V10H8M6 15H8V19H6V15M16 19V15H18V19H16Z" size="20" />
                            </v-avatar>
                            <div style="flex: 1;">
                                <div class="text-subtitle-1 font-weight-bold">
                                    {{ selectedDevice.label || selectedDevice.name }}
                                </div>
                                <div v-if="selectedDevice.label" class="text-caption text-medium-emphasis">
                                    {{ selectedDevice.name }}
                                </div>
                            </div>
                            <!-- Summary chips -->
                            <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                                <v-chip
                                    v-if="deviceSummaries.find(s => s.device.id.id === activeId)?.totalGallons"
                                    size="small"
                                    color="primary"
                                    variant="tonal"
                                    prepend-icon="M12,3.77L11.25,4.61C11.25,4.61 9.97,6.06 8.68,7.94C7.39,9.82 6,12.07 6,14.23A6,6 0 0,0 12,20.23A6,6 0 0,0 18,14.23C18,12.07 16.61,9.82 15.32,7.94C14.03,6.06 12.75,4.61 12.75,4.61L12,3.77M12,6.9C12.44,7.42 12.84,7.85 13.68,9.07C14.89,10.83 16,13.07 16,14.23C16,16.45 14.22,18.23 12,18.23C9.78,18.23 8,16.45 8,14.23C8,13.07 9.11,10.83 10.32,9.07C11.16,7.85 11.56,7.42 12,6.9Z">
                                    {{ deviceSummaries.find(s => s.device.id.id === activeId)?.totalGallons.toFixed(1) }} gal / 5j
                                </v-chip>
                            </div>
                        </v-card-title>
                    </v-card>

                    <!-- Explanation row -->
                    <div class="d-flex align-center flex-wrap mb-3" style="gap: 10px;">
                        <span class="text-caption text-medium-emphasis">Consommation cumulée par jour — repart à zéro à minuit.</span>
                        <span class="d-flex align-center text-caption text-medium-emphasis" style="gap: 4px;">
                            <span style="display:inline-block;width:12px;height:12px;border-radius:2px;background:rgba(33,150,243,0.20);border:1px solid #2196F3;" />
                            Eau
                        </span>
                        <span class="d-flex align-center text-caption text-medium-emphasis" style="gap: 4px;">
                            <span style="display:inline-block;width:12px;height:12px;border-radius:2px;background:rgba(76,175,80,0.20);border:1px solid #4CAF50;" />
                            Engrais
                        </span>
                        <span class="d-flex align-center text-caption text-medium-emphasis" style="gap: 4px;">
                            <span style="display:inline-block;width:12px;height:2px;background:#F44336;border-radius:1px;" />
                            Température serre
                        </span>
                    </div>

                    <!-- Main chart with sliders -->
                    <v-card variant="outlined" rounded="lg" style="flex: 1 1 0; min-height: 320px; display: flex; flex-direction: column;">
                        <!-- Smoothing sliders + gallons-per-run edit — top of chart card -->
                        <div class="px-3 pt-2 pb-1 d-flex flex-row align-center flex-wrap" style="gap: 16px; border-bottom: thin solid rgba(var(--v-border-color), var(--v-border-opacity));">
                            <div class="d-flex align-center" style="gap: 6px; flex: 0 1 50%; min-width: 0;">
                                <v-icon icon="M3.55 19.09L4.96 20.5L6.76 18.71L5.34 17.29M12 6C8.69 6 6 8.69 6 12S8.69 18 12 18 18 15.31 18 12C18 8.68 15.31 6 12 6M20 13H23V11H20M17.24 18.71L19.04 20.5L20.45 19.09L18.66 17.29M20.45 5L19.04 3.6L17.24 5.39L18.66 6.81M13 1H11V4H13M6.76 5.39L4.96 3.6L3.55 5L5.34 6.81L6.76 5.39M1 13H4V11H1M13 20H11V23H13" size="13" color="#FFC107" />
                                <span class="text-caption text-medium-emphasis" style="white-space: nowrap;">Lissage lux</span>
                                <v-slider
                                    v-model="luxSmoothing"
                                    :min="0"
                                    :max="10"
                                    :step="1"
                                    density="compact"
                                    color="#FFC107"
                                    track-color="rgba(255,193,7,0.25)"
                                    hide-details
                                    thumb-size="12"
                                    style="flex: 1 1 0; min-width: 0;" />
                                <span class="text-caption" style="color: #FFC107; width: 16px; text-align: right;">{{ luxSmoothing }}</span>
                            </div>
                            <div class="d-flex align-center" style="gap: 6px; flex: 0 1 50%; min-width: 0;">
                                <v-icon icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z" size="13" color="#F44336" />
                                <span class="text-caption text-medium-emphasis" style="white-space: nowrap;">Lissage temp.</span>
                                <v-slider
                                    v-model="tempSmoothing"
                                    :min="0"
                                    :max="10"
                                    :step="1"
                                    density="compact"
                                    color="#F44336"
                                    track-color="rgba(244,67,54,0.25)"
                                    hide-details
                                    thumb-size="12"
                                    style="flex: 1 1 0; min-width: 0;" />
                                <span class="text-caption" style="color: #F44336; width: 16px; text-align: right;">{{ tempSmoothing }}</span>
                            </div>
                            <!-- Gallons per run inline editor -->
                            <div class="d-flex align-center" style="gap: 6px; flex: 0 0 auto;">
                                <v-icon icon="M19,14.5C19,14.5 21,16.67 21,18A2,2 0 0,1 19,20A2,2 0 0,1 17,18C17,16.67 19,14.5 19,14.5M5,18V9A2,2 0 0,1 3,7A2,2 0 0,1 5,5V4A2,2 0 0,1 7,2H9A2,2 0 0,1 11,4V5H19A2,2 0 0,1 21,7V9L21,11A1,1 0 0,1 22,12A1,1 0 0,1 21,13H17A1,1 0 0,1 16,12A1,1 0 0,1 17,11V9H11V18H12A2,2 0 0,1 14,20V22H2V20A2,2 0 0,1 4,18H5Z" size="13" color="#2196F3" />
                                <span class="text-caption text-medium-emphasis" style="white-space: nowrap;">Gal/run</span>
                                <v-text-field
                                    v-model="gallonsPerRunEdit"
                                    type="number"
                                    density="compact"
                                    variant="outlined"
                                    hide-details
                                    :min="0"
                                    style="width: 80px;"
                                    @keyup.enter="applyGallonsPerRun" />
                                <v-btn
                                    icon
                                    size="x-small"
                                    variant="text"
                                    color="primary"
                                    :loading="isSavingGpr"
                                    :disabled="!selectedDevice || gallonsPerRunEdit === ''"
                                    @click="applyGallonsPerRun">
                                    <v-icon size="14">M15,9H5V5H15M12,19A3,3 0 0,1 9,16A3,3 0 0,1 12,13A3,3 0 0,1 15,16A3,3 0 0,1 12,19M17,3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3Z</v-icon>
                                </v-btn>
                            </div>
                        </div>
                        <div style="flex: 1 1 0; min-height: 280px; padding: 8px;">
                            <VChart :option="chartOption" autoresize style="height: 100%;" />
                        </div>
                    </v-card>
                </template>
            </div>
        </div>
    </v-card>
</template>

<style scoped>
/* ── Page header ─────────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Device panel background ──────────────────── */
.device-panel {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.12)" : "rgba(var(--v-theme-primary), 0.02)"');
}
</style>
